<template>
  <aside
    id="componentTTSPlayer"
    :aria-label="t('tts.componentTitle')"
    class="flex tts-container h-auto justify-center items-center noselect"
    :class="{
      ['tts-container--hidden']: !visibilityStore.showUI,
      ['tts-container--mediaOverlay']: wpsClient.media.isMediaOverlay,
    }"
  >
    <PreviousCommand v-slot="props">
      <MediaPlayerButton v-bind="props" />
    </PreviousCommand>

    <div v-if="showVolume">
      <input
        id="volume-range-slider"
        v-model="settingsStore.perContent.ttsVoiceVolume"
        class="appearance-none relative w-full h-1 rounded outline-none slider-thumb element-focusable"
        type="range"
        name="volume"
        min="1"
        step="1"
        max="100"
        @blur="hideVolume"
      />
      <label class="hidden" for="volume">Volume</label>
    </div>
    <MediaPlayerButton
      icon="volume_up"
      :label="t('tts.controls.volume')"
      :tool-tip="t('tts.controls.volume')"
      @click="toggleVolume()"
    />

    <StartMediaOverlayCommand v-slot="props">
      <MediaPlayerButton bigger v-bind="props" />
    </StartMediaOverlayCommand>

    <ResumeCommand v-slot="props">
      <MediaPlayerButton bigger v-bind="props" />
    </ResumeCommand>

    <PauseCommand v-slot="props">
      <MediaPlayerButton bigger v-bind="props" />
    </PauseCommand>

    <StopCommand v-slot="props">
      <MediaPlayerButton v-bind="props" />
    </StopCommand>

    <NextCommand v-slot="props">
      <MediaPlayerButton v-bind="props" />
    </NextCommand>

    <TTSSettingsCommand v-slot="props">
      <MediaPlayerButton v-bind="props" />
    </TTSSettingsCommand>
  </aside>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSettingsStore } from "@/stores/settings";
import { useUiVisibilityStore } from "@/stores/uiVisibility";

import { useWpsClient } from "@/wps/client";

import StopCommand from "@/commands/media/StopCommand.vue";
import ResumeCommand from "@/commands/media/ResumeCommand.vue";
import PauseCommand from "@/commands/media/PauseCommand.vue";
import NextCommand from "@/commands/media/NextCommand.vue";
import PreviousCommand from "@/commands/media/PreviousCommand.vue";
import StartMediaOverlayCommand from "@/commands/mediaOverlay/StartMediaOverlayCommand.vue";
import TTSSettingsCommand from "@/commands/tts/TTSSettingsCommand.vue";

import MediaPlayerButton from "./MediaPlayerButton.vue";

const { t } = useI18n();

const wpsClient = useWpsClient();

const settingsStore = useSettingsStore();
const visibilityStore = useUiVisibilityStore();
const showVolume = ref<boolean>(false);

function toggleVolume() {
  showVolume.value = !showVolume.value;
  if (showVolume.value) {
    setTimeout(() => {
      document.getElementById("volume-range-slider")?.focus();
    }, 100);
  }
}

function hideVolume() {
  setTimeout(() => {
    showVolume.value = false;
  }, 200);
}
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;
.tts-container {
  display: flex;
  height: $barHeight;
  width: 100%;
  z-index: 3;
  bottom: $barHeight;
  opacity: 1;
  align-items: center;
  justify-content: center;
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  transition:
    height 0s,
    width 0s,
    right 0.8s cubic-bezier(0, 1, 0.5, 1),
    opacity 0.8s cubic-bezier(0, 1, 0.5, 1);

  @media (min-width: $screenL) {
    width: 300px;
    padding-top: 0;
  }

  &--hidden {
    opacity: 0;
  }

  &--mediaOverlay {
    padding-top: 0;
    @media (min-width: $screenSM) {
      width: $mediaOverlayPlayer;
    }
  }

  &--overlay {
    z-index: 5;
    border: 3px solid rgba(158, 158, 158, 0.2);
  }

  &:hover {
    z-index: 5;
  }

  input[type="range"] {
    position: absolute;
    width: 80px;
    height: 7px;
    cursor: pointer;
    transform: rotate(270deg) translate(25px, 23px);
    transform-origin: left;

    background: var(--DM__theme-fill-color);
    border: 2px solid var(--DM__theme-font-color);
  }
  .slider-thumb::-webkit-slider-thumb {
    background-color: var(--DM__theme-font-color);
    width: 0.8rem;
    height: 0.8rem;
    border: 1px solid var(--DM__theme-fill-color);
  }
}
</style>
